import React, { useState } from 'react'
import ConnectButton from '../components/ConnectButton'
import { api, ApiResponse, OAuthStartResponse } from '../utils/api'

type Status = 'idle' | 'starting' | 'redirecting' | 'error'

const Connect: React.FC = () => {
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string>('')

  const startOAuth = async () => {
    try {
      setStatus('starting')
      setError('')

      // Get tenant ID from URL params or default
      const urlParams = new URLSearchParams(window.location.search)
      const tenantId = urlParams.get('tenant') || 'default'

      const response: ApiResponse<OAuthStartResponse> = await api.startOAuth(tenantId)
      
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to start OAuth')
      }

      setStatus('redirecting')
      
      // Small delay to show the "redirecting" state
      setTimeout(() => {
        window.location.href = response.data!.consentUrl
      }, 500)

    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
      
      // Auto-reset to idle after 3 seconds
      setTimeout(() => {
        if (status === 'error') {
          setStatus('idle')
          setError('')
        }
      }, 3000)
    }
  }

  const isDisabled = status !== 'idle' && status !== 'error'

  return (
    <div className="container">
      <div className="header">
        <div className="quickbooks-logo">QB</div>
        <h1>Connect QuickBooks</h1>
        <p>Securely connect your QuickBooks Online account to start syncing your financial data.</p>
      </div>

      <ConnectButton 
        onClick={startOAuth}
        disabled={isDisabled}
        status={status}
      />

      {error && (
        <div className="error-message">
          <strong>Connection Failed:</strong> {error}
        </div>
      )}

      <div className="info-section">
        <h3>What happens next?</h3>
        <p>
          After clicking Connect, you'll be redirected to QuickBooks Online to authorize access. 
          Once authorized, we'll automatically set up your data sync and return you here with a confirmation.
        </p>
      </div>

      <div className="info-section">
        <h3>Security & Privacy</h3>
        <p>
          We use OAuth 2.0 for secure authentication. Your QuickBooks credentials are never stored on our servers. 
          You can revoke access at any time from your QuickBooks account settings.
        </p>
      </div>
    </div>
  )
}

export default Connect